/**
 * Rock Off Retail — Model view toggle (MV-1)
 *
 * A two-button Grid / Model control, rendered by the shared
 * snippets/ro-view-toggle.liquid (mounted outside any USF skeleton — see
 * that snippet's own comment for why). Switching to "Model" flips a class
 * on <html> (`ro-model-view`); pure CSS (injected by this script) then
 * swaps which whole `.media` wrapper is visible inside each theme card
 * that has one (`.card--has-model-image`, from snippets/card-product.liquid
 * — fix round 1: the model image lives in its own sibling `.media`
 * wrapper, never inside the garment's `.media--hover-effect` block, so
 * this script's toggle never fights that block's own hover/crossfade CSS).
 * Cards without a model image are never touched, so they always keep their
 * normal garment image — no broken/empty cards.
 *
 * For USF cards (Work Item 2, pasted separately into Sobooster's own
 * Customization -> JavaScript editor — never done from this repo) this
 * script's job stops at flipping the same shared `ro-model-view` class on
 * <html>. It never reads or writes `window.usf.templates` — RVue compiles
 * that template string once at page load, so patching it afterwards is
 * inert (see SCOPE.md, BUILD-LOG.md 18 Aug 2026 "Attempt 1").
 *
 * USF GATE (MV-T1, 29 Sep 2026): the toggle is no longer gated on the
 * window._roModelViewUsfReady flag (read once at DOMContentLoaded, so a late
 * USF render was missed). It now reveals itself when at least one card in
 * #usf_container carries a model image. One debounced MutationObserver on
 * document.body re-resolves #usf_container on every check (USF can replace
 * that node when it mounts; 30 Sep 2026 fix) and re-scans after every USF
 * re-render such as filter, sort, paging or infinite scroll, plus re-checks
 * at 400/1500/4000 ms and on load. The flag is ignored (harmless if the USF
 * paste still sets it).
 *
 * DATA CONTRACT with the USF product card template (see
 * _sop/state/model-view-build/usf/product-card-model-image-snippet.md):
 *   <img class="ro-card-model-media" data-ro-model-src="https://..."
 *        loading="lazy" alt="Model view" hidden>
 * placed anywhere inside the card. Selector used here:
 *   img.ro-card-model-media[data-ro-model-src]
 * Only https URLs count. In Model view this script swaps the src of the
 * card's primary image (first other <img> in the card root) to that URL,
 * keeping the original src/srcset in data-ro-orig-* so Grid view restores
 * it. On image error the card silently reverts. Cards without the element
 * are never touched. Theme cards (card-product.liquid) keep their own
 * sibling-wrapper mechanism (div.ro-card-model-media inside
 * .card--has-model-image) and are not swapped by src.
 *
 * 2 Oct 2026 (Sohail): the DEFAULT view is Model (see safeGetStoredMode:
 * only a click on the toggle stores a choice). On USF pages the toggle mount
 * is moved into USF's own `.usf-sr-config` count/sort row (placeToggles) and
 * the collection hero description is clamped to one line, with a real
 * More/Less button only when it is truly cut off (setupHeroDescription). Hub grids are untouched by the placement code.
 *
 * 6 Oct 2026 (Sohail): Model view hover mirrors Product view. Hovering a
 * card in Model view fades the model shot out and shows the product shot
 * with the same 1.03 zoom, on desktop with a real mouse only (same gate as
 * the Product view crossfade). Theme cards do it in CSS (model wrapper over
 * the garment wrapper); USF cards get one decorative product-shot copy
 * (img.ro-mv-garment) after the swapped picture, see ensureGarment().
 *
 * The localStorage choice is wrapped in try/catch throughout: storage can
 * throw (private browsing, quota, disabled) and none of that should ever
 * break the page.
 */
(function () {
  'use strict';

  var STORAGE_KEY = 'ro-view-mode';
  var MODE_GRID = 'grid';
  var MODE_MODEL = 'model';
  var DEFAULT_MODE = MODE_MODEL;
  var ACTIVE_CLASS = 'ro-model-view';
  var STYLE_ID = 'ro-model-view-toggle-styles';
  var USF_MODEL_SELECTOR = 'img.ro-card-model-media[data-ro-model-src]';
  var CARD_ROOT_SELECTOR = '.card-wrapper, .usf-sr-product, li, article';
  var RESCAN_DELAY_MS = 80;
  var GARMENT_CLASS = 'ro-mv-garment';
  /* USF List view draws the product picture as a <usf-bgset> DIV (inline
     background-image), not an <img>; see swapBgToModel. */
  var USF_BG_MAIN_SELECTOR = '.usf-main-img.usf-bgset';
  var USF_BG_SECOND_SELECTOR = '.usf-second-img.usf-bgset';

  /* 2 Oct 2026 (Sohail): the DEFAULT view is Model. Only a click on the toggle
     ever writes STORAGE_KEY (safeSetStoredMode is called from the click
     handler and nowhere else, and it never ran on load in any earlier
     version either), so a stored 'grid' or 'model' is always an explicit
     visitor choice and keeps winning. No stored value, an unknown value or
     unreadable storage all mean the default: Model. The key is therefore NOT
     versioned: no automatic value exists that could pin anyone to Product. */
  function safeGetStoredMode() {
    try {
      var value = window.localStorage.getItem(STORAGE_KEY);
      if (value === MODE_GRID) return MODE_GRID;
      return DEFAULT_MODE;
    } catch (err) {
      return DEFAULT_MODE;
    }
  }

  function safeSetStoredMode(mode) {
    try {
      window.localStorage.setItem(STORAGE_KEY, mode);
    } catch (err) {
      /* private browsing / quota / disabled storage — silently ignore,
         the toggle still works for the rest of this page view */
    }
  }

  function injectStyles() {
    if (document.getElementById(STYLE_ID)) return;

    var style = document.createElement('style');
    style.id = STYLE_ID;
    style.textContent = [
      /* Mount wrapper — right-aligns the toggle above whichever grid it
         sits in front of (USF's own .usf-sr-config row right-aligns its
         .usf-views the same way via margin-left: auto on its ::before, see
         assets/usf.css). Small vertical margin so it reads as its own
         control row rather than crowding the grid/title above or below
         it — every mount point (USF sections, rockoff-hub-grid.liquid,
         featured-collection.liquid) renders this from the one shared
         snippets/ro-view-toggle.liquid, so one rule here covers all four. */
      '.ro-view-toggle-mount {',
      '  display: flex;',
      '  justify-content: flex-end;',
      '  margin: 1.2rem 0;',
      '}',
      /* [hidden] is set on the MOUNT, not the inner .ro-view-toggle (see
         snippets/ro-view-toggle.liquid) — an explicit override is still
         needed because `.ro-view-toggle-mount { display: flex }` above is
         an author-stylesheet rule and therefore beats the user-agent
         stylesheet's own `[hidden] { display: none }` default. */
      '.ro-view-toggle-mount[hidden] {',
      '  display: none;',
      '}',
      /* Toggle control — two-button group, matches the USF skeleton's
         compact .usf-views footprint so it doesn't shift that row. */
      '.ro-view-toggle {',
      '  display: inline-flex;',
      '  align-items: center;',
      '  gap: 0;',
      '  border: 1px solid rgba(0, 0, 0, 0.15);',
      '  border-radius: var(--buttons-radius-outset, 4px);',
      '  overflow: hidden;',
      '}',
      '.ro-view-toggle__btn {',
      '  appearance: none;',
      '  -webkit-appearance: none;',
      '  border: none;',
      '  background: transparent;',
      '  cursor: pointer;',
      '  font: inherit;',
      '  font-size: 1.3rem;',
      '  line-height: 1;',
      '  padding: 0.7rem 1.2rem;',
      '  color: inherit;',
      '}',
      '.ro-view-toggle__btn + .ro-view-toggle__btn {',
      '  border-left: 1px solid rgba(0, 0, 0, 0.15);',
      '}',
      '.ro-view-toggle__btn[aria-pressed="true"] {',
      '  background: rgba(0, 0, 0, 0.08);',
      '  font-weight: 600;',
      '}',
      '.ro-view-toggle__btn:focus-visible {',
      '  outline: 2px solid currentColor;',
      '  outline-offset: -2px;',
      '}',
      /* Card image swap — scoped to cards that actually carry a model
         image (card--has-model-image, set server-side in card-product.liquid)
         so a card with no model shot is never affected and keeps its
         normal garment image.

         Fix round 1 (24 Sep 2026), finding 1 + finding 3: the model image
         now renders in its OWN sibling `.media.ro-card-model-media`
         wrapper, never inside `.media--hover-effect` (see
         card-product.liquid's own comment on this — that was the whole
         point of moving it: base.css's
         `.media.media--hover-effect > img + img { opacity: 0 }` used to
         hide the model image at all times, including when this script set
         its `display: block !important` — display and opacity are
         independent, so the image stayed invisible (opacity: 0) even once
         shown (display: block), i.e. a blank card in Model view. Toggling
         the WHOLE wrapper's display (never scoped under
         `.media--hover-effect`, so that opacity:0 rule can never match it)
         avoids the opacity clash entirely; no opacity/transform override is
         needed here since nothing in the theme's CSS targets
         `.ro-card-model-media` or its <img> at all. !important overrides
         the inline style="display: none;" both wrappers ship with (so
         neither flashes over the other before this stylesheet loads). */
      '.card--has-model-image .ro-card-model-media {',
      '  display: none;',
      '}',
      'html.' + ACTIVE_CLASS + ' .card--has-model-image .ro-card-model-media {',
      '  display: block !important;',
      '}',
      'html.' + ACTIVE_CLASS + ' .card--has-model-image .media.media--hover-effect {',
      '  display: none !important;',
      '}',
      /* USF cards (MV-T1): the template's model <img> is only a data
         carrier and is never shown itself; this script swaps the primary
         image's src instead. object-fit keeps the model shot inside the
         garment image's box (no layout shift), and the hover second image
         is suppressed while a card shows its model shot. */
      USF_MODEL_SELECTOR + ' {',
      '  display: none !important;',
      '}',
      'img[data-ro-model-swapped] {',
      '  object-fit: cover;',
      '}',
      'html.' + ACTIVE_CLASS + ' img[data-ro-model-swapped] ~ img:not(.' + GARMENT_CLASS + ') {',
      '  display: none !important;',
      '}',
      /* The theme fades the first image out on card hover
         (component-card.css: `.card-wrapper:hover .media.media--hover-effect
         > img:first-child:not(:only-child) { opacity: 0 }` plus a zoom).
         The swapped image IS that first image, so without this it would
         vanish on hover and show an empty box when there is no product-shot
         copy to fade to (no garment URL, or a touch screen). Keep it fully
         visible and still. */
      'html.' + ACTIVE_CLASS + ' .card-wrapper:hover .media.media--hover-effect > img[data-ro-model-swapped]:not([data-ro-mv-ready]) {',
      '  opacity: 1 !important;',
      '  transform: none;',
      '}',
      /* 6 Oct 2026 (Sohail): Model view hover mirrors Product view. Product
         view fades to the second picture (often the model shot) with a 1.03
         zoom; Model view now fades from the model shot to the product shot
         with the same zoom. Same gate as the Product view crossfade in
         component-card.css (desktop width + a real mouse), so touch screens
         keep a still model shot and never download the hidden product shot.
         The product-shot copy (USF) is display:none outside this gate, so a
         lazy image is not fetched on phones. */
      'img.' + GARMENT_CLASS + ' {',
      '  display: none !important;',
      '}',
      '@media screen and (min-width: 990px) and (hover: hover) and (pointer: fine) {',
      /* Theme cards: the model wrapper sits over the garment wrapper (both are
         absolute, full-size `.card .media` boxes) and fades out on hover. The
         garment's own second picture stays hidden (and unfetched) so hover
         always lands on the product shot, never on a back print or a second
         model shot. */
      '  html.' + ACTIVE_CLASS + ' .card--has-model-image .media.media--hover-effect {',
      '    display: block !important;',
      '  }',
      '  html.' + ACTIVE_CLASS + ' .card--has-model-image .media.media--hover-effect > img + img {',
      '    display: none !important;',
      '  }',
      '  html.' + ACTIVE_CLASS + ' .card--has-model-image .media.media--hover-effect > img:first-child {',
      '    opacity: 1 !important;',
      '    transition: opacity 0.4s cubic-bezier(0.25, 0.46, 0.45, 0.94), transform var(--duration-long, 0.5s) ease;',
      '  }',
      '  html.' + ACTIVE_CLASS + ' .card--has-model-image .ro-card-model-media {',
      '    z-index: 1;',
      '    transition: opacity 0.4s cubic-bezier(0.25, 0.46, 0.45, 0.94);',
      '  }',
      '  html.' + ACTIVE_CLASS + ' .card-wrapper:hover .card--has-model-image .ro-card-model-media {',
      '    opacity: 0;',
      '  }',
      '  html.' + ACTIVE_CLASS + ' .card-wrapper:hover .card--has-model-image .media.media--hover-effect > img:first-child {',
      '    transform: scale(1.03);',
      '  }',
      /* USF cards: the model shot is the swapped first picture; the product
         shot is our own copy right after it (see ensureGarment). */
      '  html.' + ACTIVE_CLASS + ' img.' + GARMENT_CLASS + ' {',
      '    display: block !important;',
      '    position: absolute;',
      '    top: 0;',
      '    left: 0;',
      '    width: 100%;',
      '    height: 100%;',
      '    object-fit: cover;',
      '    opacity: 0;',
      '    transition: opacity 0.4s cubic-bezier(0.25, 0.46, 0.45, 0.94), transform var(--duration-long, 0.5s) ease;',
      '  }',
      '  html.' + ACTIVE_CLASS + ' .card-wrapper:hover img[data-ro-model-swapped][data-ro-mv-ready],',
      '  html.' + ACTIVE_CLASS + ' .usf-sr-product:hover img[data-ro-model-swapped][data-ro-mv-ready] {',
      '    opacity: 0 !important;',
      '  }',
      '  html.' + ACTIVE_CLASS + ' .card-wrapper:hover img.' + GARMENT_CLASS + ',',
      '  html.' + ACTIVE_CLASS + ' .usf-sr-product:hover img.' + GARMENT_CLASS + ' {',
      '    opacity: 1 !important;',
      '    transform: scale(1.03);',
      '  }',
      '}',
      /* Reduced motion: no fade, same as the theme's .motion-reduce pictures
         (base.css). The USF copy carries .motion-reduce itself. */
      '@media (prefers-reduced-motion) {',
      '  html.' + ACTIVE_CLASS + ' .card--has-model-image .ro-card-model-media,',
      '  html.' + ACTIVE_CLASS + ' img[data-ro-model-swapped] {',
      '    transition: none !important;',
      '  }',
      '}'
    ].join('\n');

    document.head.appendChild(style);
  }

  function setButtonsState(toggle, mode) {
    var buttons = toggle.querySelectorAll('[data-ro-view-btn]');
    for (var i = 0; i < buttons.length; i++) {
      var btn = buttons[i];
      var pressed = btn.getAttribute('data-ro-view-btn') === mode;
      btn.setAttribute('aria-pressed', pressed ? 'true' : 'false');
    }
  }

  function applyMode(mode, toggles) {
    document.documentElement.classList.toggle(ACTIVE_CLASS, mode === MODE_MODEL);
    for (var i = 0; i < toggles.length; i++) {
      setButtonsState(toggles[i], mode);
    }
    var usfContainer = getUsfContainer();
    if (usfContainer) syncUsfCards(usfContainer, mode);
  }

  function hasModelCards() {
    return document.querySelector('.card--has-model-image') !== null;
  }

  function getUsfContainer() {
    /* #usf_container is this same section's own live render target
       (snippets/usf.liquid). Its presence means this page is a USF
       collection/search grid, per SCOPE.md part C. */
    return document.getElementById('usf_container');
  }

  function validModelUrl(modelEl) {
    if (!modelEl) return '';
    var url = modelEl.getAttribute('data-ro-model-src') || '';
    if (!/^https:\/\/\S+$/i.test(url)) return '';
    /* data-ro-model-failed holds the URL that errored, so a reused node whose
       carrier now points at a different product's model shot is not blocked. */
    return modelEl.getAttribute('data-ro-model-failed') === url ? '' : url;
  }

  function findPrimaryImage(modelEl) {
    var root = modelEl.closest ? modelEl.closest(CARD_ROOT_SELECTOR) : null;
    if (!root) return null;
    var imgs = root.querySelectorAll('img');
    for (var i = 0; i < imgs.length; i++) {
      if (imgs[i] !== modelEl && !imgs[i].classList.contains('ro-card-model-media') &&
          !imgs[i].classList.contains(GARMENT_CLASS)) return imgs[i];
    }
    return null;
  }

  /* 11 Oct 2026 (Sohail): USF List view. The list template draws the product
     picture as a <usf-bgset> DIV with an inline background-image (written by
     USF once the picture has loaded, data-loaded="true"), not as an <img>,
     so findPrimaryImage found nothing and Model view did nothing on list
     pages. For such a card the swap writes the model shot into the main
     DIV's background-image and the garment into the hover DIV's, so USF's
     own list hover fades to the product shot exactly as Grid view does.

     Ownership (Codex review, 11 Oct): the two DIVs are handled independently.
     Each keeps its own original and is only restored while it still shows
     what WE wrote (main: data-ro-model-swapped; hover: data-ro-hover-written).
     A background USF has rewritten since is USF's again and is adopted as
     the new original on the next sweep. The hover is reconciled on every
     sweep (syncHover), so a hover that loads after the main, or that USF
     rewrites, still ends up showing the garment. A DIV USF has not loaded
     yet is left alone (USF would overwrite the style on load); the observer
     wakes on style and data-loaded changes, so the sweep returns once it
     has. The model shot is preloaded before it replaces the garment: a URL
     that fails marks its carriers data-ro-model-failed (same fallback as the
     IMG path) and the garment stays. */
  function findBgMain(modelEl) {
    var root = modelEl.closest ? modelEl.closest(CARD_ROOT_SELECTOR) : null;
    if (!root) return null;
    var main = root.querySelector(USF_BG_MAIN_SELECTOR);
    return main && main.tagName !== 'IMG' ? main : null;
  }

  function findBgSecond(main) {
    var root = main.closest ? main.closest(CARD_ROOT_SELECTOR) : null;
    var second = root ? root.querySelector(USF_BG_SECOND_SELECTOR) : null;
    return second && second.tagName !== 'IMG' ? second : null;
  }

  function bgUrl(el) {
    var v = el && el.style ? el.style.backgroundImage : '';
    var m = /url\((['"]?)(.*?)\1\)/.exec(v || '');
    return m ? m[2] : '';
  }

  /* USF writes protocol-relative URLs (//cdn...), the carrier holds https://. */
  function sameUrl(a, b) {
    if (!a || !b) return false;
    return a === b || a.replace(/^https?:/, '') === b.replace(/^https?:/, '');
  }

  function showsModel(el, url) {
    if (!el || !url) return false;
    if (el.tagName === 'IMG') return el.getAttribute('src') === url;
    /* A DIV also has to still belong to the product we swapped it for. */
    return sameUrl(bgUrl(el), url) && pidOf(el) === (el.getAttribute('data-ro-pid') || '');
  }

  /* Which product a card shows: USF stamps the card root with the product id
     (`:data-usf-pid="product.id"`). Our originals belong to that product; if
     USF reuses the node for another product, they are stale and must never
     be written back or adopted. '' when the template carries no id. */
  function pidOf(el) {
    var root = el && el.closest ? el.closest('.usf-sr-product') : null;
    return root ? (root.getAttribute('data-usf-pid') || '') : '';
  }

  function hoverOf(main) {
    /* The hover we wrote is remembered on the main, so a main USF detached
       can still release its hover (Codex r2, P2). */
    var second = main.__roMvSecond;
    if (second && second.isConnected) return second;
    return findBgSecond(main);
  }

  /* Hover DIV: show the garment (the main's original) while the main shows
     the model shot. Idempotent; safe on every sweep. A hover marked stale
     (data-ro-hover-stale = the garment we wrote for a product this node no
     longer shows) is left alone until USF writes something else into it. */
  function syncHover(main) {
    var second = findBgSecond(main);
    if (!second) return;
    var garment = main.getAttribute('data-ro-orig-bg');
    var garmentUrl = main.getAttribute('data-ro-orig-url');
    if (!garment || !garmentUrl) return;
    var cur = bgUrl(second);
    var stale = second.getAttribute('data-ro-hover-stale');
    if (stale) {
      if (sameUrl(cur, stale)) return; /* still our old write: wait for USF */
      second.removeAttribute('data-ro-hover-stale');
      second.removeAttribute('data-ro-orig-bg');
    }
    var written = second.getAttribute('data-ro-hover-written');
    if (written && sameUrl(cur, written)) {
      if (sameUrl(written, garmentUrl)) { main.__roMvSecond = second; second.__roMvOwner = main; return; } /* in place */
    } else {
      /* USF owns the current picture: it is the original, once loaded. */
      if (!cur || second.getAttribute('data-loaded') !== 'true') return;
      second.setAttribute('data-ro-orig-bg', second.style.backgroundImage);
    }
    second.style.backgroundImage = garment;
    second.setAttribute('data-ro-hover-written', garmentUrl);
    main.__roMvSecond = second;
    second.__roMvOwner = main; /* only this main may release or drop it (Fable review, fix 1) */
  }

  function restoreHover(main) {
    var second = hoverOf(main);
    main.__roMvSecond = null;
    if (!second) return;
    /* A hover another main has since adopted (USF hot-swapped the main DIV
       and the new one swapped in the same sweep) is that main's now. */
    if (second.__roMvOwner && second.__roMvOwner !== main) return;
    second.__roMvOwner = null;
    var ours = second.getAttribute('data-ro-hover-written') || second.getAttribute('data-ro-hover-stale');
    if (ours && sameUrl(bgUrl(second), ours)) {
      var orig = second.getAttribute('data-ro-orig-bg');
      if (orig !== null) second.style.backgroundImage = orig;
    }
    second.removeAttribute('data-ro-hover-written');
    second.removeAttribute('data-ro-hover-stale');
    second.removeAttribute('data-ro-orig-bg');
  }

  function clearBgAttrs(main) {
    main.removeAttribute('data-ro-model-swapped');
    main.removeAttribute('data-ro-orig-bg');
    main.removeAttribute('data-ro-orig-url');
    main.removeAttribute('data-ro-pid');
    main.classList.remove('ro-model-swapped');
  }

  function restoreBg(main) {
    var swapped = main.getAttribute('data-ro-model-swapped');
    if (swapped && sameUrl(bgUrl(main), swapped)) {
      var orig = main.getAttribute('data-ro-orig-bg');
      if (orig !== null) main.style.backgroundImage = orig;
    }
    restoreHover(main);
    clearBgAttrs(main);
  }

  /* The node now shows another product (Codex r2, P1): our originals are
     that other product's pictures, so nothing is written back. Whatever we
     wrote and USF has not yet replaced is marked stale, so it is neither
     adopted as an original nor swapped over until USF writes the new
     product's own picture. */
  function dropBgState(main) {
    var swapped = main.getAttribute('data-ro-model-swapped');
    if (swapped && sameUrl(bgUrl(main), swapped)) main.setAttribute('data-ro-main-stale', swapped);
    var second = hoverOf(main);
    main.__roMvSecond = null;
    if (second && (!second.__roMvOwner || second.__roMvOwner === main)) {
      var written = second.getAttribute('data-ro-hover-written');
      if (written && sameUrl(bgUrl(second), written)) second.setAttribute('data-ro-hover-stale', written);
      /* A hover already marked stale keeps its original for restoreHover
         (this can run twice for one release: swap path, then the registry's
         released path; Fable review, fix 2). */
      else if (!second.hasAttribute('data-ro-hover-stale')) second.removeAttribute('data-ro-orig-bg');
      second.removeAttribute('data-ro-hover-written');
      second.__roMvOwner = null;
    }
    clearBgAttrs(main);
  }

  /* Model shots are preloaded before a background swap: url -> true once
     loaded, false while pending. A failed URL marks every carrier holding
     it as failed (validModelUrl then skips them) and is forgotten. */
  var bgLoadState = {};
  var wakeSweep = null; /* set by watchUsf: the debounced USF sweep */

  function bgReady(url) {
    if (bgLoadState[url] === true) return true;
    if (bgLoadState[url] === false) return false;
    bgLoadState[url] = false;
    var probe = new Image();
    probe.onload = function () {
      bgLoadState[url] = true;
      if (wakeSweep) wakeSweep();
    };
    probe.onerror = function () {
      delete bgLoadState[url];
      var carriers = document.querySelectorAll(USF_MODEL_SELECTOR);
      for (var i = 0; i < carriers.length; i++) {
        if (carriers[i].getAttribute('data-ro-model-src') === url) carriers[i].setAttribute('data-ro-model-failed', url);
      }
      if (wakeSweep) wakeSweep();
    };
    probe.src = url;
    return false;
  }

  function swapBgToModel(modelEl, url) {
    var main = findBgMain(modelEl);
    if (!main) return null;
    var pidNow = pidOf(main);
    var swapped = main.getAttribute('data-ro-model-swapped');
    if (swapped) {
      var pidThen = main.getAttribute('data-ro-pid') || '';
      if (pidNow !== pidThen) {
        dropBgState(main); /* another product in this node: forget, write nothing */
        return null;
      }
      if (sameUrl(bgUrl(main), swapped) && swapped === url) {
        syncHover(main);
        return main; /* already showing it */
      }
      /* Same product, new model shot (or USF rewrote the picture): put the
         garment back and swap again below from the product's own picture. */
      var restoredMain = sameUrl(bgUrl(main), swapped);
      restoreBg(main);
      /* Without a product id we cannot tell a new model shot from a product
         change: do not adopt a picture we restored ourselves; the restore
         write wakes the observer and the next sweep adopts USF's picture. */
      if (restoredMain && !pidNow) return null;
    }
    var current = bgUrl(main);
    if (!current || main.getAttribute('data-loaded') !== 'true') return null; /* USF has not loaded it yet */
    var stale = main.getAttribute('data-ro-main-stale');
    if (stale) {
      if (sameUrl(current, stale)) return null; /* still our old model shot: wait for USF */
      main.removeAttribute('data-ro-main-stale');
    }
    if (!bgReady(url)) return null; /* preload first; the load wakes the sweep */
    main.setAttribute('data-ro-orig-bg', main.style.backgroundImage);
    main.setAttribute('data-ro-orig-url', current);
    main.setAttribute('data-ro-model-swapped', url);
    main.setAttribute('data-ro-pid', pidNow);
    main.classList.add('ro-model-swapped');
    main.style.backgroundImage = 'url("' + url + '")';
    syncHover(main);
    return main;
  }

  /* 6 Oct 2026 (Sohail): Model view hover shows the product shot, as Product
     view hover shows the model shot. USF cards only have one <img> we can
     use (its src now holds the model shot), so the product shot is a copy we
     own: one decorative <img class="ro-mv-garment"> inserted right after the
     swapped picture, holding the original src/srcset. CSS keeps it
     display:none except on desktop with a real mouse, where it fades in with
     the 1.03 zoom on hover. Idempotent: safe to call on every sweep. */
  function removeGarment(img) {
    var g = img.__roMvGarment;
    if (g && g.parentNode) g.parentNode.removeChild(g);
    img.__roMvGarment = null;
    img.removeAttribute('data-ro-mv-ready');
  }

  function ensureGarment(img) {
    if (!img) return;
    if (img.tagName !== 'IMG') { syncHover(img); return; } /* USF list DIV: the hover DIV holds the garment */
    var src = img.getAttribute('data-ro-orig-src') || '';
    var srcset = img.getAttribute('data-ro-orig-srcset') || '';
    /* __roMvGarmentFailed holds a URL that errored, so a broken product shot
       is not re-added (and re-fetched) on every sweep. */
    if (!src || !img.parentNode || img.__roMvGarmentFailed === src) {
      if (img.__roMvGarment) removeGarment(img);
      return;
    }
    var g = img.__roMvGarment;
    /* In place = directly after its picture with the right URL. Anything else
       (USF moved the picture after the copy, or into another parent) is
       repaired below: a picture that lands after the copy would match
       base.css `img + img { opacity: 0 }` and leave a blank card. */
    if (g && g.previousSibling === img && g.getAttribute('src') === src) {
      markGarmentReady(img, g);
      return;
    }
    if (!g) {
      g = document.createElement('img');
      g.className = GARMENT_CLASS + ' motion-reduce';
      g.setAttribute('alt', '');
      g.setAttribute('aria-hidden', 'true');
      g.setAttribute('loading', 'lazy');
      g.setAttribute('decoding', 'async');
      g.addEventListener('load', function () {
        if (img.__roMvGarment === g) markGarmentReady(img, g);
      });
      g.addEventListener('error', function () {
        /* broken product-shot URL: drop the copy, the model shot stays put */
        if (img.__roMvGarment !== g) return;
        img.__roMvGarmentFailed = g.getAttribute('src');
        removeGarment(img);
      });
      img.__roMvGarment = g;
    }
    var sizes = img.getAttribute('sizes') || '';
    if (sizes) { if (g.getAttribute('sizes') !== sizes) g.setAttribute('sizes', sizes); }
    else if (g.hasAttribute('sizes')) g.removeAttribute('sizes');
    if (srcset) { if (g.getAttribute('srcset') !== srcset) g.setAttribute('srcset', srcset); }
    else if (g.hasAttribute('srcset')) g.removeAttribute('srcset');
    if (g.getAttribute('src') !== src) g.setAttribute('src', src);
    if (g.previousSibling !== img) {
      img.parentNode.insertBefore(g, img.nextSibling);
    }
    markGarmentReady(img, g);
  }

  /* The hover fade is armed (data-ro-mv-ready) only once the product shot
     has actually loaded, so a hover on a slow connection never fades the
     model shot away to an empty box. Until then the model shot is pinned by
     the :not([data-ro-mv-ready]) rule. On phones the copy is display:none,
     never loads, and the fade is never armed. */
  function markGarmentReady(img, g) {
    var ready = g.complete && g.naturalWidth > 0;
    /* A data attribute, not a class: USF's Vue may rewrite a bound class
       list on re-render, but never touches data-ro-* attributes. */
    if (ready !== img.hasAttribute('data-ro-mv-ready')) {
      if (ready) img.setAttribute('data-ro-mv-ready', '');
      else img.removeAttribute('data-ro-mv-ready');
    }
  }

  /* Remove any product-shot copy that no longer sits directly after the
     picture that owns it (its picture was removed or replaced by a USF
     re-render). Runs after every sweep, when every live copy has already
     been put back in place. */
  function removeOrphanGarments(container) {
    var copies = container.querySelectorAll('img.' + GARMENT_CLASS);
    for (var i = 0; i < copies.length; i++) {
      var owner = copies[i].previousSibling;
      if (!owner || owner.__roMvGarment !== copies[i]) {
        copies[i].parentNode.removeChild(copies[i]);
      }
    }
  }

  function clearSwapState(img) {
    removeGarment(img);
    img.removeAttribute('data-ro-model-swapped');
    img.removeAttribute('data-ro-orig-src');
    img.removeAttribute('data-ro-orig-srcset');
    img.removeAttribute('data-ro-orig-alt');
    img.classList.remove('ro-model-swapped');
  }

  /* Put the garment back ONLY while the node still shows our model src.
     If the src differs, Vue has already patched a new product into this
     reused node (its own src/srcset/alt), so writing the stored originals
     back would show the previous product: just drop our swap state. */
  function restoreImage(img) {
    if (img && img.tagName !== 'IMG') { /* USF list DIV */
      /* A connected main whose card now shows another product must not get
         that other product's garment written back; a detached one only
         releases its hover. */
      if (img.isConnected && pidOf(img) !== (img.getAttribute('data-ro-pid') || '')) return dropBgState(img);
      return restoreBg(img);
    }
    var swapped = img.getAttribute('data-ro-model-swapped');
    if (swapped && img.getAttribute('src') === swapped) {
      var origSrc = img.getAttribute('data-ro-orig-src');
      var origSrcset = img.getAttribute('data-ro-orig-srcset');
      var origAlt = img.getAttribute('data-ro-orig-alt');
      if (origSrc) img.setAttribute('src', origSrc);
      if (origSrcset) img.setAttribute('srcset', origSrcset);
      if (origAlt !== null) img.setAttribute('alt', origAlt);
    }
    clearSwapState(img);
  }

  function onSwappedImageError(event) {
    var img = event.currentTarget;
    var swapped = img.getAttribute('data-ro-model-swapped');
    if (!swapped || img.getAttribute('src') !== swapped) return;
    var root = img.closest ? img.closest(CARD_ROOT_SELECTOR) : null;
    var modelEl = root ? root.querySelector(USF_MODEL_SELECTOR) : null;
    if (modelEl) modelEl.setAttribute('data-ro-model-failed', swapped);
    restoreImage(img); /* broken model URL: fall back to the garment image */
  }

  function swapToModel(modelEl) {
    var url = validModelUrl(modelEl);
    if (!url) return null;
    var img = findPrimaryImage(modelEl);
    if (!img) return swapBgToModel(modelEl, url); /* USF list card: background DIV */

    var swapped = img.getAttribute('data-ro-model-swapped');
    if (swapped) {
      if (img.getAttribute('src') === swapped) {
        if (swapped === url) return img; /* already showing this model shot */
        restoreImage(img); /* node reused for another product: start clean */
      } else {
        clearSwapState(img); /* USF rewrote src on a reused node: adopt it as original */
      }
    }

    img.setAttribute('data-ro-orig-src', img.getAttribute('src') || '');
    img.setAttribute('data-ro-orig-srcset', img.getAttribute('srcset') || '');
    img.setAttribute('data-ro-orig-alt', img.getAttribute('alt') || '');
    /* Card height needs no extra lock: the theme card's .media box already
       fixes the ratio and the swapped img is object-fit: cover inside it. */
    var modelAlt = modelEl.getAttribute('alt');
    if (modelAlt) img.setAttribute('alt', modelAlt);

    if (img.getAttribute('data-ro-err-wired') !== '1') {
      img.addEventListener('error', onSwappedImageError);
      img.setAttribute('data-ro-err-wired', '1');
    }

    img.removeAttribute('srcset');
    img.setAttribute('data-ro-model-swapped', url);
    img.classList.add('ro-model-swapped');
    img.setAttribute('src', url);
    ensureGarment(img);
    return img;
  }

  /* Every image this script has swapped, keyed by the carrier that caused the
     swap: carrier -> { url, img }. A carrier that disappears (node removed or
     reused for a product with no usable model shot) or turns invalid has its
     image restored from here, so nothing depends on counting. */
  var swaps = typeof Map === 'function' ? new Map() : null;
  var syncEpoch = 0;

  /* Bring every USF card in line with the current mode and return how many
     cards carry a usable model image. Safe to call repeatedly.

     1 Oct 2026 (Load more got slower on every click): this used to redo the
     whole per-card job (closest + querySelectorAll('img') per card) for EVERY
     card on every re-render. Now a carrier whose swap is still in place
     (same url, same connected image still showing it) is skipped; only new
     carriers, carriers with a changed URL, and mode changes do DOM work.
     What is left per card is an attribute read and an integer compare. Stale
     swaps are found through the swaps registry (each valid carrier is stamped
     with this pass's epoch; any registry entry without the stamp is stale). */
  function syncUsfCards(container, mode) {
    var models = container.querySelectorAll(USF_MODEL_SELECTOR);
    var count = 0;
    var i;
    var epoch = ++syncEpoch;
    var released = []; /* images whose previous owner let go this pass */
    for (i = 0; i < models.length; i++) {
      var modelEl = models[i];
      var url = validModelUrl(modelEl);
      if (!url) continue;
      count++;
      modelEl.__roMvEpoch = epoch;
      if (mode !== MODE_MODEL) continue;
      var rec = swaps ? swaps.get(modelEl) : null;
      if (rec && rec.url === url && rec.img.isConnected && showsModel(rec.img, url)) {
        ensureGarment(rec.img); /* cheap check; re-adds the copy if a re-render dropped it */
        continue; /* swap still in place: nothing else to do for this card */
      }
      var img = swapToModel(modelEl);
      if (rec && rec.img !== img) released.push(rec.img); /* carrier moved to another image */
      if (swaps) {
        if (img) swaps.set(modelEl, { url: url, img: img });
        else swaps.delete(modelEl);
      }
    }
    if (swaps) {
      var stale = [];
      var claimed = new Set();
      swaps.forEach(function (entry, carrier) {
        if (mode !== MODE_MODEL || carrier.__roMvEpoch !== epoch) stale.push(carrier);
        else claimed.add(entry.img);
      });
      for (i = 0; i < stale.length; i++) {
        released.push(swaps.get(stale[i]).img);
        swaps.delete(stale[i]);
      }
      /* Restore only images no live carrier owns after this pass: when USF
         replaces a carrier but keeps the card's primary image, the new
         carrier has already claimed it, and restoring it through the old
         carrier's entry would undo the swap. */
      for (i = 0; i < released.length; i++) {
        if (!claimed.has(released[i])) restoreImage(released[i]);
      }
    } else {
      /* No Map (very old browser): fall back to the per-image sweep. */
      var swappedImgs = container.querySelectorAll('[data-ro-model-swapped]');
      for (i = 0; i < swappedImgs.length; i++) {
        var root = swappedImgs[i].closest ? swappedImgs[i].closest(CARD_ROOT_SELECTOR) : null;
        var stillHasModel = root && validModelUrl(root.querySelector(USF_MODEL_SELECTOR));
        if (mode !== MODE_MODEL || !stillHasModel) restoreImage(swappedImgs[i]);
      }
    }
    removeOrphanGarments(container);
    return count;
  }

  function wireToggle(toggle, currentModeRef) {
    toggle.addEventListener('click', function (event) {
      var btn = event.target.closest ? event.target.closest('[data-ro-view-btn]') : null;
      if (!btn || !toggle.contains(btn)) return;

      var nextMode = btn.getAttribute('data-ro-view-btn') === MODE_MODEL ? MODE_MODEL : MODE_GRID;
      if (nextMode === currentModeRef.mode) return;

      currentModeRef.mode = nextMode;
      safeSetStoredMode(nextMode);
      applyMode(nextMode, currentModeRef.toggles);
    });
  }

  function setMountsHidden(toggleList, hidden) {
    for (var i = 0; i < toggleList.length; i++) {
      var parent = toggleList[i].parentElement;
      /* fix round 1: `hidden` lives on the .ro-view-toggle-mount wrapper
         (snippets/ro-view-toggle.liquid), not the inner element, so the row
         collapses to zero height on pages that never qualify. */
      if (parent && parent.classList.contains('ro-view-toggle-mount') && parent.hidden !== hidden) {
        parent.hidden = hidden;
      }
    }
  }

  /* 2 Oct 2026 (Sohail): on USF pages the toggle row sits INSIDE USF's own
     count / sort row (`.usf-sr-config`), right after the product count
     (`.usf-sr-summary`), instead of taking a row of its own above the filters.
     USF (RVue) re-renders that row on filter, sort and paging, and a node we
     put inside it dies with the old row, so this runs on EVERY check (cheap:
     two lookups and a sibling compare) and puts the same mount node back into
     the new row. The mount is only ever moved, never recreated, so the click
     handler wired in activate() keeps working. It only inserts between USF's
     own siblings and never removes or edits any USF node.

     Only the mount that belongs to the USF grid is ever moved (Codex B r1,
     HIGH): findUsfMount() picks it structurally (same parent as
     #usf_container, i.e. the one the section renders right before it, or any
     mount inside the container's own section; featured-collection and hub
     mounts are never candidates) and remembers it, so a second toggle on the
     page is never touched. Placement is idempotent: when the mount already
     sits right after the summary there is NO DOM write at all, so the
     observer is never re-triggered by its own work.
     Returns true when the mount sits in the count / sort row. */
  function findUsfMount(state, container) {
    if (state.usfMount) return state.usfMount;
    var section = container.closest ? container.closest('.shopify-section') : null;
    var best = null;
    var loose = [];
    for (var i = 0; i < state.toggleList.length; i++) {
      var mount = state.toggleList[i].parentElement;
      if (!mount || !mount.classList.contains('ro-view-toggle-mount')) continue;
      if (mount.classList.contains('ro-view-toggle-mount--featured') ||
          mount.classList.contains('ro-view-toggle-mount--hub')) continue;
      if (mount.parentNode === container.parentNode) { best = mount; break; }
      if (section && section.contains(mount)) { best = best || mount; continue; }
      loose.push(mount);
    }
    if (!best && !section && loose.length === 1) best = loose[0];
    if (best) {
      state.usfMount = best;
      best.setAttribute('data-ro-usf-mount', '1');
    }
    return best;
  }

  function placeToggles(state) {
    var container = getUsfContainer();
    if (!container) return false;
    var summary = container.querySelector('.usf-sr-config > .usf-sr-summary');
    if (!summary || !summary.parentNode) return false;
    var mount = findUsfMount(state, container);
    if (!mount) return false;
    if (mount.previousElementSibling === summary && mount.parentNode === summary.parentNode) return true;
    if (!mount.classList.contains('ro-view-toggle-mount--inline')) mount.classList.add('ro-view-toggle-mount--inline');
    summary.parentNode.insertBefore(mount, summary.nextSibling);
    return true;
  }

  function activate(state) {
    if (state.active) return;
    state.active = true;
    injectStyles();
    for (var i = 0; i < state.toggleList.length; i++) {
      wireToggle(state.toggleList[i], state.currentModeRef);
    }
    applyMode(state.currentModeRef.mode, state.toggleList);
  }

  function debounce(fn, delay) {
    var timer = null;
    return function () {
      if (timer !== null) window.clearTimeout(timer);
      timer = window.setTimeout(function () {
        timer = null;
        fn();
      }, delay);
    };
  }

  /* 30 Sep 2026: USF can REPLACE #usf_container when it mounts, so an
     observer bound to the node found at init watched a detached element and
     the toggle never appeared (carriers present, mount still hidden). Watch
     document.body instead and re-resolve the container on every check; also
     re-check a few times after load in case no mutation fires after the
     first render. */
  /* True when a mutation record only added/removed our own toggle mount(s). */
  function isOwnMove(rec) {
    var n = 0;
    var lists = [rec.addedNodes, rec.removedNodes];
    for (var l = 0; l < lists.length; l++) {
      var list = lists[l];
      for (var i = 0; list && i < list.length; i++) {
        var node = list[i];
        if (!node.classList || !node.classList.contains('ro-view-toggle-mount')) return false;
        /* A mount USF detached (not our move) must wake the sweep. */
        if (!node.isConnected) return false;
        n++;
      }
    }
    return n > 0;
  }

  function watchUsf(state) {
    var seen = false;
    var check = debounce(function () {
      var container = getUsfContainer();
      if (!container) return;
      var count = syncUsfCards(container, state.currentModeRef.mode);
      var placed = placeToggles(state);
      if (count > 0) {
        seen = true;
        activate(state);
        /* Wait for the count / sort row so the toggle never appears on its own
           row and then jumps; the last timed re-check (4 s) shows it where it
           is if USF never renders that row (list layout, no results chrome). */
        if (placed || state.lastChance) setMountsHidden(state.toggleList, false);
      } else if (seen) {
        setMountsHidden(state.toggleList, !hasModelCards());
      }
    }, RESCAN_DELAY_MS);
    wakeSweep = check;
    var target = document.body || getUsfContainer();
    if (typeof MutationObserver !== 'undefined' && target) {
      new MutationObserver(function (records) {
        /* Only wake up for changes inside the grid (or one that replaces it):
           cart drawer, timers and sliders elsewhere on the page do not matter.
           The sweep itself adds and removes no nodes except the one idempotent
           mount move, which isOwnMove() filters out. */
        var container = getUsfContainer();
        if (!container) return; /* not mounted yet: the mount record wakes us */
        var relevant = false;
        for (var r = 0; r < records.length; r++) {
          if (isOwnMove(records[r])) continue; /* our own placement: never re-trigger */
          var t = records[r].target;
          if (container.contains(t) || (t.contains && t.contains(container))) {
            relevant = true;
            break;
          }
        }
        if (!relevant) return;
        check();
      /* 6 Oct 2026: also wake on src / carrier URL changes. USF can reuse a
         card node for another product by patching attributes only (no child
         nodes added or removed); without this the card kept the previous
         product's model shot and hover copy until some later re-render. Our
         own src writes wake one extra sweep, which finds everything in place. */
      }).observe(target, {
        childList: true,
        subtree: true,
        attributes: true,
        attributeFilter: ['src', 'data-ro-model-src', 'style', 'data-loaded']
      });
    }
    [400, 1500, 4000].forEach(function (ms) {
      window.setTimeout(function () {
        if (ms === 4000) state.lastChance = true;
        check();
      }, ms);
    });
    if (window.addEventListener) window.addEventListener('load', check);
    check();
  }

  /* 2 Oct 2026 (Sohail): collection hero description is clamped to one line
     by CSS (-webkit-line-clamp on the element itself, so inline links and
     <strong> survive; assets/rockoff-plp-pdp.css, "above-the-fold tightening").
     JS measures REAL truncation while collapsed (scrollHeight > clientHeight)
     and only then shows a separate real <button> "More"/"Less" after the
     description (aria-expanded, aria-controls). Links the clamp cuts off get
     tabindex="-1" while collapsed and are restored on expand. If the text
     fits: no button, no tabindex changes. Re-measured on width change and
     after every collapse, so a widened window drops the button again.
     Labels: window._roCollectionMoreLabel / _roCollectionLessLabel when the
     theme sets them (locale keys rockoff.collection.more / .less), else English. */
  function setupHeroDescription() {
    var body = document.body;
    if (!body || !body.classList.contains('template-collection')) return;
    var desc = document.querySelector('.collection-hero:not(.collection-hero--with-image) .collection-hero__description');
    if (!desc || desc.getAttribute('data-ro-hero-wired') === '1') return;
    desc.setAttribute('data-ro-hero-wired', '1');
    var OPEN = 'ro-hero-desc--open';
    var HAS = 'ro-hero-desc--has-toggle';
    var btn = null;
    var lastWidth = -1;
    if (!desc.id) desc.id = 'ro-hero-desc';

    function label(open) {
      return open ? (window._roCollectionLessLabel || 'Less') : (window._roCollectionMoreLabel || 'More');
    }
    function ensureButton() {
      if (btn) return btn;
      btn = document.createElement('button');
      btn.type = 'button';
      btn.className = 'ro-hero-desc-toggle';
      btn.setAttribute('aria-controls', desc.id);
      btn.addEventListener('click', function () {
        var open = !desc.classList.contains(OPEN);
        desc.classList.toggle(OPEN, open);
        btn.setAttribute('aria-expanded', open ? 'true' : 'false');
        btn.textContent = label(open);
        measure(); /* re-measure: button and tabindex follow the real layout */
      });
      desc.parentNode.insertBefore(btn, desc.nextSibling);
      return btn;
    }
    /* Links whose first line box starts below the clamp: invisible when collapsed. */
    function clippedLinks() {
      var out = [];
      var links = desc.querySelectorAll('a');
      var bottom = desc.getBoundingClientRect().top + desc.clientHeight;
      for (var i = 0; i < links.length; i++) {
        var rects = links[i].getClientRects();
        if (!rects.length || rects[0].top >= bottom - 1) out.push(links[i]);
      }
      return out;
    }
    function setTabs(off) {
      var links = desc.querySelectorAll('a');
      for (var i = 0; i < links.length; i++) {
        var a = links[i];
        var want = off.indexOf(a) > -1;
        var has = a.getAttribute('data-ro-tab-off') === '1';
        if (want && !has) {
          /* remember the original tabindex (or its absence) to restore exactly */
          var orig = a.getAttribute('tabindex');
          a.setAttribute('data-ro-tab-orig', orig === null ? '' : orig);
          a.setAttribute('data-ro-tab-had', orig === null ? '0' : '1');
          a.setAttribute('tabindex', '-1');
          a.setAttribute('data-ro-tab-off', '1');
        } else if (!want && has) {
          if (a.getAttribute('data-ro-tab-had') === '1') a.setAttribute('tabindex', a.getAttribute('data-ro-tab-orig'));
          else a.removeAttribute('tabindex');
          a.removeAttribute('data-ro-tab-off');
          a.removeAttribute('data-ro-tab-orig');
          a.removeAttribute('data-ro-tab-had');
        }
      }
    }
    function measure() {
      var wasOpen = desc.classList.contains(OPEN);
      if (wasOpen) desc.classList.remove(OPEN); /* measure COLLAPSED, restore below */
      var cut = desc.scrollHeight > desc.clientHeight + 1;
      var off = cut ? clippedLinks() : [];
      lastWidth = desc.clientWidth;
      var open = cut && wasOpen;
      if (open) desc.classList.add(OPEN);
      desc.classList.toggle(HAS, cut);
      if (cut) {
        var b = ensureButton();
        b.hidden = false;
        b.setAttribute('aria-expanded', open ? 'true' : 'false');
        b.textContent = label(open);
      } else if (btn) {
        btn.hidden = true;
      }
      setTabs(open ? [] : off);
    }
    measure();
    if (typeof ResizeObserver === 'function') {
      new ResizeObserver(function () {
        if (desc.clientWidth !== lastWidth) measure(); /* width only: open/close changes height */
      }).observe(desc);
    } else {
      var resizeTimer = null;
      window.addEventListener('resize', function () {
        if (resizeTimer !== null) window.clearTimeout(resizeTimer);
        resizeTimer = window.setTimeout(measure, 150);
      });
    }
    window.addEventListener('load', measure);
  }

  function init() {
    setupHeroDescription();
    var toggles = document.querySelectorAll('[data-ro-view-toggle]');
    if (!toggles.length) return;

    var toggleList = Array.prototype.slice.call(toggles);
    var state = {
      active: false,
      toggleList: toggleList,
      currentModeRef: { mode: safeGetStoredMode(), toggles: toggleList }
    };

    if (hasModelCards()) {
      activate(state);
      setMountsHidden(toggleList, false);
    }
    /* else: leave every toggle hidden, default Grid stays in effect */

    /* Always install the watcher (late-mounting #usf_container); every check
       looks the container up and returns when it is not there. */
    watchUsf(state);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
